import { StockModel } from '@/http/models/Stock/Stock'
import { AlertTriangle, ArrowLeftRight, Package as InStockIcon, TrendingDown } from 'lucide-react'
import { toast } from 'sonner'

import { useState } from 'react'

import { useFetchStockLevels } from '@/hooks/features/stock'
import { useAuth } from '@/contexts/AuthContext'
import type { StockHttpParams } from '@/types'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { PageHeader, StatsCards } from '@/components/atoms'

import { LowStockAlerts } from './LowStockAlerts'
import { PurchaseDialog } from './PurchaseDialog'
import { StockHistoryTable } from './StockHistoryTable'
import { StockLevels } from './StockLevels'
import { TransferDialog } from './TransferDialog'

export const InventoryFeature = () => {
  const { loading, items, stats } = useFetchStockLevels()
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false)
  const [purchaseLoading, setPurchaseLoading] = useState(false)
  const { can } = useAuth()
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferLoading, setTransferLoading] = useState(false)

  const alertCount = stats.lowStock + stats.outOfStock

  const handleCreatePurchase = async (body: any) => {
    setPurchaseLoading(true)
    const res = await StockModel.purchase({ body })
    if (res.success) {
      toast.success('Purchase recorded successfully')
      setPurchaseDialogOpen(false)
    } else {
      toast.error((res.error?.data as any)?.message || 'Failed to record purchase')
    }
    setPurchaseLoading(false)
  }

  const handleTransfer = async (body: StockHttpParams.CreateTransfer['body']) => {
    setTransferLoading(true)
    const res = await StockModel.transfer({ body })
    if (res.success) {
      toast.success('Stock transferred')
      setTransferOpen(false)
    } else {
      toast.error((res.error?.data as any)?.message || 'Failed to transfer stock')
    }
    setTransferLoading(false)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inventory"
        description="Stock levels across your products."
        onAddClick={() => setPurchaseDialogOpen(true)}
        addPermission="stocks:purchase"
        addButtonText="New purchase"
        actions={
          can('stocks:transfer') ? (
            <Button variant="outline" onClick={() => setTransferOpen(true)}>
              <ArrowLeftRight />
              Transfer stock
            </Button>
          ) : undefined
        }
      />

      <StatsCards
        stats={[
          { title: 'In stock', value: loading ? '—' : stats.inStock, icon: InStockIcon },
          { title: 'Low stock', value: loading ? '—' : stats.lowStock, icon: AlertTriangle },
          { title: 'Out of stock', value: loading ? '—' : stats.outOfStock, icon: TrendingDown },
        ]}
      />

      <Card className="overflow-hidden">
        <Tabs defaultValue="stock-levels">
          <div className="overflow-x-auto border-b border-border-subtle p-2">
            <TabsList>
              <TabsTrigger value="stock-levels">Stock levels</TabsTrigger>
              <TabsTrigger value="alerts" className="gap-1.5">
                Low stock
                {alertCount > 0 && (
                  <Badge tone="error" className="tabular-nums">
                    {alertCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">Stock history</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="stock-levels" className="mt-0">
            <StockLevels items={items} loading={loading} />
          </TabsContent>

          <TabsContent value="alerts" className="mt-0">
            <LowStockAlerts items={items} loading={loading} />
          </TabsContent>

          <TabsContent value="history" className="mt-0">
            <StockHistoryTable onOpenPurchaseDialog={() => setPurchaseDialogOpen(true)} />
          </TabsContent>
        </Tabs>
      </Card>

      <PurchaseDialog
        open={purchaseDialogOpen}
        onOpenChange={setPurchaseDialogOpen}
        onSubmit={handleCreatePurchase}
        loading={purchaseLoading}
      />

      <TransferDialog open={transferOpen} onOpenChange={setTransferOpen} onSubmit={handleTransfer} loading={transferLoading} />
    </div>
  )
}
